// src/components/WOEPayment/hooks/useAgreementManager.js
// Updated to work with stateless backend endpoints

import { useState, useCallback, useEffect, useRef } from 'react';
import { isIsraelUser } from '../../../../utils/locationUtils';
import { normalizeOrderType } from '../../../../utils/orderTypeUtils';
import { sendAgreementSMS, sendAgreementReminder } from '../../../../services/api';
import { API_URL } from '../../../../config/api';
import { getCurrentTimestamp } from '../../../../utils/dateUtils';

const useAgreementManager = (customerInfo, orderNo, totals, paymentSummary, orderSubmissionUtils, onOrderNoUpdate, wigData, savedAgreementState = null) => {

    const pollingIntervalRef = useRef(null);
    const isPollingFetchRef = useRef(false); // prevents overlapping signature status checks
    const [isGeneratingOrderNo, setIsGeneratingOrderNo] = useState(false);
    const [currentSigningToken, setCurrentSigningToken] = useState(null);

    // 🔧 NEW: Restore saved agreement state if available
    const initialAgreementState = savedAgreementState || {
        // Overall status
        status: 'pending', // 'pending', 'sms_sent', 'sms_signed', 'in_person_signed', 'expired', 'failed'
        isRequired: false,
        isSigned: false,

        // SMS workflow
        sms: {
            enabled: true,
            sent: false,
            sentAt: null,
            phoneNumber: null,
            signingToken: null,
            expiresAt: null,
            reminderCount: 0,
            lastStatusCheck: null
        },

        // In-person backup
        inPerson: {
            modalOpen: false,
            fallbackReason: null // 'no_phone', 'sms_failed', 'customer_preference', 'expired'
        },

        // Agreement data
        signedData: null,
        signedAt: null,
        signingMethod: null, // 'sms_remote', 'in_person_digital'

        // Error handling
        error: null,
        retryCount: 0
    };

    // Agreement state management
    const [agreementState, setAgreementState] = useState(initialAgreementState);

    // Polling reference for SMS status checks
    const maxRetries = 3;

    const ensureRealOrderNumber = useCallback(async () => {
        const hasTempOrderNo = orderNo && orderNo.toString().startsWith('TEMP-');

        if (hasTempOrderNo) {
            console.log('🔢 Agreement needs real order number, generating...');
            
            setIsGeneratingOrderNo(true);
            
            try {
                // Use the order submission utilities if available, otherwise use direct API call
                let newOrderNo;
                if (orderSubmissionUtils?.ensureRealOrderNumber) {
                    newOrderNo = await orderSubmissionUtils.ensureRealOrderNumber();
                } else {
                    // Fallback to direct API call
                    const response = await fetch(`${API_URL}/api/generate-order-number`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            tempOrderNo: orderNo,
                            orderType: customerInfo.orderType
                        })
                    });
                    
                    // ✅ ADD THIS ERROR HANDLING HERE:
                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error('❌ Order generation API error:', {
                            status: response.status,
                            statusText: response.statusText,
                            errorText: errorText
                        });
                        throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
                    }
                    // ✅ END OF ADDITION
                    
                    const result = await response.json();
                    
                    if (result.success) {
                        newOrderNo = result.newOrderNo;
                    } else {
                        throw new Error(result.error || 'Failed to generate order number');
                    }
                }
                
                console.log('✅ Generated real order number for agreement:', newOrderNo);
                
                // Update the order number in parent component
                if (onOrderNoUpdate) {
                    onOrderNoUpdate(newOrderNo);
                }
                
                return newOrderNo;
            } catch (error) {
                console.error('❌ Error generating order number for agreement:', error);
                throw error;
            } finally {
                setIsGeneratingOrderNo(false);
            }
        }
        
        // Return the existing order number if no generation needed
        return orderNo;
    }, [customerInfo?.orderType, orderNo, orderSubmissionUtils, onOrderNoUpdate]);


    // Determine if agreement is required
    const checkAgreementRequirement = useCallback(() => {
        // "Aidel New Wig" follows the same agreement rules as "New Wig".
        const orderType = normalizeOrderType(customerInfo?.orderType);
        const required = orderType === 'New Wig' || orderType === 'New Sale Wig';
        
        setAgreementState(prev => ({
            ...prev,
            isRequired: required
        }));
        
        return required;
    }, [customerInfo?.orderType]);

    // Generate unique signing token
    const generateSigningToken = (realOrderNo) => {
        return `${realOrderNo}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    };

        
    const sendSMSAgreement = useCallback(async () => {
        // Clear any existing polling AND reset token state
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            console.log('🧹 Cleared old polling before starting new SMS');
        }
    
        setCurrentSigningToken(null);
    
        if (!customerInfo?.phone) {
            setAgreementState(prev => ({
                ...prev,
                status: 'failed',
                error: 'No phone number available for SMS',
                inPerson: {
                    ...prev.inPerson,
                    fallbackReason: 'no_phone'
                }
            }));
            return { success: false, error: 'No phone number' };
        }
    
        try {
            setAgreementState(prev => ({
                ...prev,
                status: 'sending_sms',
                error: null
            }));
    
            // Ensure we have a real order number for the agreement
            let realOrderNo = orderNo;
            try {
                realOrderNo = await ensureRealOrderNumber();
                console.log('✅ Using order number for SMS agreement:', realOrderNo);
            } catch (orderError) {
                console.error('❌ Failed to generate order number for agreement:', orderError);
                setAgreementState(prev => ({
                    ...prev,
                    status: 'failed',
                    error: 'Failed to generate order number for agreement',
                    inPerson: {
                        ...prev.inPerson,
                        fallbackReason: 'order_generation_failed'
                    }
                }));
                return { success: false, error: 'Failed to generate order number' };
            }
    
            const signingToken = generateSigningToken(realOrderNo);
            console.log('🆕 Generated new signing token:', signingToken);
            
            setCurrentSigningToken(signingToken);
    
            const totalAmount = parseFloat(totals.total) + parseFloat(paymentSummary.totalCardFees || 0);
    
            // ✅ MAKE SURE TO INCLUDE ORDER TYPE AND WIG DETAILS in the SMS agreement data
            const result = await sendAgreementSMS({
                customerName: customerInfo.customerName,
                customerPhone: customerInfo.phone,
                customerNo: customerInfo.customerNo,
                orderNo: realOrderNo,
                orderType: customerInfo.orderType, // ✅ CRITICAL: Include order type
                totalAmount: totalAmount.toFixed(2),
                signingToken: signingToken,
                expiresInHours: 24,
                companyName: 'Dini Wigs',
                agreementType: 'purchase_agreement',
                version: 'v2.24.25',
                userLocation: isIsraelUser() ? 'Israel' : 'US',
                processingMode: isIsraelUser() ? 'LOCAL' : 'QUICKBOOKS',
                // ✅ Include wig details - check multiple property names from your actual data structure
                wigBarCode: customerInfo.wigBarCode || customerInfo.barcode || wigData?.barcode || wigData?.wigBarCode || '',
                wigLength: customerInfo.wigLength || customerInfo.hairLength || wigData?.hairLength || wigData?.wigLength || wigData?.Wig_Length || '',
                wigType: customerInfo.wigType || wigData?.wigType || ''
            });
    
            if (result.success) {
                setAgreementState(prev => ({
                    ...prev,
                    status: 'sms_sent',
                    sms: {
                        ...prev.sms,
                        sent: true,
                        sentAt: getCurrentTimestamp(),
                        phoneNumber: customerInfo.phone,
                        signingToken: signingToken,
                        expiresAt: result.expiresAt,
                        reminderCount: 0
                    }
                }));
    
                console.log('🔄 Starting polling for token:', signingToken);
                startPollingForSignature(signingToken);
                
                return { 
                    success: true, 
                    message: `Agreement sent via SMS to ${customerInfo.phone}`,
                    signingToken: signingToken,
                    expiresAt: result.expiresAt,
                    signingUrl: result.signingUrl,
                    orderNo: realOrderNo
                };
            } else {
                throw new Error(result.error || 'Failed to send SMS');
            }
    
        } catch (error) {
            console.error('SMS Agreement sending failed:', error);
            
            setCurrentSigningToken(null);
            
            setAgreementState(prev => ({
                ...prev,
                status: 'failed',
                error: error.message,
                retryCount: prev.retryCount + 1,
                inPerson: {
                    ...prev.inPerson,
                    fallbackReason: 'sms_failed'
                }
            }));
    
            return { success: false, error: error.message };
        }
    }, [customerInfo, orderNo, totals, paymentSummary, ensureRealOrderNumber]);


    // ✅ Update the startPollingForSignature function to validate the token:
    const startPollingForSignature = useCallback((signingToken) => {
        // Clear any existing polling
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        console.log(`🔄 Starting polling for token: ${signingToken}`);
        console.log(`🔍 Current stored token: ${currentSigningToken}`);

        // ✅ Validate that we're polling for the correct token
        if (currentSigningToken && currentSigningToken !== signingToken) {
            console.warn('⚠️ Token mismatch detected:', {
                currentToken: currentSigningToken,
                pollingToken: signingToken
            });
        }

        // ✅ Store the token we're polling for
        const currentToken = signingToken;


    pollingIntervalRef.current = setInterval(async () => {
        if (isPollingFetchRef.current) return;
        isPollingFetchRef.current = true;
        try {
            const API_BASE_URL = API_URL ||
                (window.location.hostname === 'dinitracker-v2.pages.dev' 
                    ? 'https://dinitracker-api-production-8e1a.up.railway.app'  // Production
                    : 'https://dinitracker-api-production.up.railway.app');      // Staging
            
            const apiUrl = `${API_BASE_URL}/api/agreements/status/${currentToken}`;
            
            console.log(`🔍 Polling URL: ${apiUrl}`);
            const response = await fetch(apiUrl);
            
            console.log(`🔍 Response status: ${response.status}`);
            console.log(`🔍 Response headers:`, response.headers.get('content-type'));

            setAgreementState(prev => ({
                ...prev,
                sms: {
                    ...prev.sms,
                    lastStatusCheck: getCurrentTimestamp()
                }
            }));

            if (response.status === 404) {
                console.log('❌ Agreement not found (404) - stopping polling');
                console.log('❌ Searched for token:', currentToken);
                clearInterval(pollingIntervalRef.current);
                setAgreementState(prev => ({
                    ...prev,
                    status: 'failed',
                    error: 'Agreement not found'
                }));
                return;
            }

            if (response.status === 410) {
                console.log('⏰ Agreement expired (410) - stopping polling');
                clearInterval(pollingIntervalRef.current);
                setAgreementState(prev => ({
                    ...prev,
                    status: 'expired',
                    inPerson: {
                        ...prev.inPerson,
                        fallbackReason: 'expired'
                    }
                }));
                return;
            }

            if (!response.ok) {
                const responseText = await response.text();
                console.log(`❌ Non-OK response (${response.status}): ${responseText.substring(0, 200)}`);
                return;
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const responseText = await response.text();
                console.error(`❌ Expected JSON but got: ${contentType}`);
                console.error(`Response preview: ${responseText.substring(0, 200)}`);
                console.error(`Full URL attempted: ${apiUrl}`);
                return;
            }

            const statusData = await response.json();
            console.log('📊 Polling response for token', currentToken, ':', statusData);

            // ✅ Check for completion
            // signed=true means the customer actually signed — independent of Google Drive.
            // A Drive failure must never block the order from proceeding.
            const isComplete = statusData.signed ||
                            statusData.googleDriveSaved ||
                            statusData.data?.signed;

            if (isComplete) {
                const driveOk = statusData.googleDriveSaved || !!statusData.googleDriveFile;
                console.log(`✅ Agreement signed - order can proceed (Drive saved: ${driveOk})`);
                clearInterval(pollingIntervalRef.current);
                
                setAgreementState(prev => ({
                    ...prev,
                    status: 'sms_signed',
                    isSigned: true,
                    signedData: statusData.data?.agreementData || statusData.agreementData || statusData,
                    signedAt: statusData.data?.signedAt || statusData.signedAt,
                    signingMethod: 'sms_remote',
                    googleDriveFile: statusData.data?.googleDriveFile || statusData.googleDriveFile
                }));

                console.log('✅ Remote SMS agreement signed and saved:', statusData);
            } else {
                console.log('⏳ Agreement not signed yet, continuing to poll...');
                console.log('⏳ Status details:', {
                    signed: statusData.signed,
                    googleDriveSaved: statusData.googleDriveSaved,
                    hasGoogleDriveFile: !!(statusData.googleDriveFile || statusData.data?.googleDriveFile),
                    dataExists: !!statusData.data
                });
            }

        } catch (error) {
            console.error('❌ Error checking signature status:', error);

            if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
                console.error('❌ Received HTML instead of JSON - check server endpoint');
            }
        } finally {
            isPollingFetchRef.current = false;
        }
        }, 10000); // Check every 10 seconds

    }, [currentSigningToken]);

    
    const openInPersonSigning = useCallback(async (reason = 'customer_preference') => {
        try {
            console.log('🔍 DEBUG - Starting in-person signing with order:', orderNo);
            
            let realOrderNo = orderNo;
            const hasTempOrderNo = orderNo && orderNo.toString().startsWith('TEMP-');
    
            if (hasTempOrderNo) {
                console.log('🔢 In-person agreement needs real order number, generating...');
                setIsGeneratingOrderNo(true);
                
                try {
                    // ✅ FIX: Use the same logic as SMS (try orderSubmissionUtils first)
                    let newOrderNo;
                    if (orderSubmissionUtils?.ensureRealOrderNumber) {
                        console.log('🔧 Using orderSubmissionUtils.ensureRealOrderNumber for in-person');
                        newOrderNo = await orderSubmissionUtils.ensureRealOrderNumber();
                    } else {
                        console.log('🔧 Using direct API call for in-person');
                        // Use the same API call as in ensureRealOrderNumber
                        const API_BASE_URL = process.env.NODE_ENV === 'production' 
                            ? 'https://dinitracker-api-production-8e1a.up.railway.app'
                            : 'http://localhost:5001';
                            
                        const response = await fetch(`${API_URL}/api/generate-order-number`, {

                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                tempOrderNo: orderNo,
                                orderType: customerInfo.orderType
                            })
                        });
                        
                        const result = await response.json();
                        
                        if (result.success) {
                            newOrderNo = result.newOrderNo;
                        } else {
                            throw new Error(result.error || 'Failed to generate order number');
                        }
                    }
                    
                    console.log('✅ Generated real order number for in-person agreement:', newOrderNo);
                    
                    // Update the order number in parent component
                    if (onOrderNoUpdate) {
                        console.log('🔄 Updating parent with in-person order number:', newOrderNo);
                        onOrderNoUpdate(newOrderNo);
                    }
                    
                    realOrderNo = newOrderNo; // ✅ Use the generated number
                    
                } catch (orderError) {
                    console.error('❌ Failed to generate order number for in-person agreement:', orderError);
                    setAgreementState(prev => ({
                        ...prev,
                        error: 'Failed to generate order number for agreement'
                    }));
                    return;
                } finally {
                    setIsGeneratingOrderNo(false);
                }
            }
    
            console.log('🔍 DEBUG - Final order number for in-person agreement:', realOrderNo);
    
            setAgreementState(prev => ({
                ...prev,
                inPerson: {
                    modalOpen: true,
                    fallbackReason: reason,
                    realOrderNo: realOrderNo
                }
            }));
        } catch (error) {
            console.error('Error preparing in-person signing:', error);
            setAgreementState(prev => ({
                ...prev,
                error: error.message || 'Failed to prepare agreement'
            }));
            setIsGeneratingOrderNo(false);
        }
    }, [orderSubmissionUtils, orderNo, customerInfo?.orderType, onOrderNoUpdate]); // ✅ Add missing dependencies
    

    const closeInPersonSigning = useCallback(() => {
        setAgreementState(prev => ({
            ...prev,
            inPerson: {
                ...prev.inPerson,
                modalOpen: false
            }
        }));
    }, []);


    
    const handleInPersonSignature = useCallback(async (signedData) => {
        console.log('📝 Processing in-person signature:', signedData);
        
        // Get the real order number from stored state
        const realOrderNo = agreementState.inPerson.realOrderNo || orderNo;
        
        console.log('🔍 DEBUG - Using order number for agreement:', realOrderNo);
        console.log('🔍 DEBUG - Signature data type:', typeof signedData.signature);
        console.log('🔍 DEBUG - Is base64 image:', signedData.signature?.startsWith('data:image/'));
        
        setAgreementState(prev => ({
            ...prev,
            status: 'processing_signature',
            inPerson: {
                ...prev.inPerson,
                modalOpen: false
            }
        }));
    
        try {
            const agreementData = {
                customerInfo: {
                    customerName: customerInfo.customerName,
                    customerPhone: customerInfo.phone || '',
                    customerNo: customerInfo.customerNo || '',
                    customerEmail: customerInfo.customerEmail || '',
                    // ✅ Include wig details at customer level for fallback - use actual property names
                    wigBarCode: customerInfo.wigBarCode || customerInfo.barcode || wigData?.barcode || wigData?.wigBarCode || '',
                    wigLength: customerInfo.wigLength || customerInfo.hairLength || wigData?.hairLength || wigData?.wigLength || ''
                },
                orderData: {
                    orderNo: realOrderNo, // 🔧 Use the real order number
                    orderType: customerInfo.orderType,
                    total: parseFloat(totals.total) + parseFloat(paymentSummary.totalCardFees || 0),
                    orderDate: getCurrentTimestamp(),
                    // ✅ Include wig details for PDF generation - use actual property names from your data
                    wigBarCode: customerInfo.wigBarCode || customerInfo.barcode || wigData?.barcode || wigData?.wigBarCode || '',
                    wigLength: customerInfo.wigLength || customerInfo.hairLength || wigData?.hairLength || wigData?.wigLength || '',
                    wigType: customerInfo.wigType || wigData?.wigType || customerInfo.orderType || ''
                },
                signatureData: {
                    // 🔧 FIX: Map signature field to signatureData for consistency
                    signatureData: signedData.signature, // This is the base64 image
                    signature: signedData.customerName || customerInfo.customerName, // Customer name
                    signedAt: getCurrentTimestamp(),
                    method: 'in_person_digital',
                    signatureType: 'digital_canvas',
                    signatureMethod: 'digital_canvas',
                    documentVersion: 'v2.24.25',
                    ipAddress: 'in-person-terminal',
                    signingMethod: 'in_person_digital',
                    auditTrail: JSON.stringify({
                        platform: 'in-person-terminal',
                        userAgent: navigator.userAgent,
                        screenResolution: `${window.screen.width}x${window.screen.height}`,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        signatureMethod: 'digital_canvas',
                        orderType: customerInfo.orderType // ✅ Include order type in audit trail
                    })
                },
                signedAt: getCurrentTimestamp(),
                signingMethod: 'in_person_digital'
            };
    
            const API_BASE_URL = process.env.NODE_ENV === 'production' 
                ? 'https://dinitracker-api-production-8e1a.up.railway.app'
                : 'http://localhost:5001';
    
            console.log('📤 Sending in-person agreement with real order number:', realOrderNo);
            console.log('📤 Agreement type:', customerInfo.orderType);
    
            const response = await fetch(`${API_URL}/api/agreements/save-in-person`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(agreementData)
            });
    
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to save agreement: ${response.status} ${errorText}`);
            }
    
            const result = await response.json();
            console.log('✅ In-person agreement saved to Google Drive with real order number:', result);
    
            setAgreementState(prev => ({
                ...prev,
                status: 'in_person_signed',
                isSigned: true,
                signedData: signedData,
                signedAt: getCurrentTimestamp(),
                signingMethod: 'in_person_digital',
                googleDriveFile: result.googleDriveFile || null,
                inPerson: {
                    modalOpen: false,
                    fallbackReason: null,
                    realOrderNo: null // Clear stored order number
                }
            }));
    
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
    
            console.log('✅ In-person agreement signed and saved to Google Drive:', {
                orderNo: realOrderNo,
                orderType: customerInfo.orderType,
                googleDriveFile: result.googleDriveFile,
                signedAt: getCurrentTimestamp()
            });
    
        } catch (error) {
            console.error('❌ Error saving in-person agreement:', error);
            
            const realOrderNo = agreementState.inPerson.realOrderNo || orderNo;
            
            setAgreementState(prev => ({
                ...prev,
                status: 'in_person_signed',
                isSigned: true,
                signedData: signedData,
                signedAt: getCurrentTimestamp(),
                signingMethod: 'in_person_digital',
                googleDriveError: {
                    message: error.message,
                    timestamp: getCurrentTimestamp()
                },
                inPerson: {
                    modalOpen: false,
                    fallbackReason: null,
                    realOrderNo: null
                }
            }));
    
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
    
            console.log('⚠️ In-person agreement signed locally but Google Drive save failed:', error.message);
        }
    }, [customerInfo, wigData, agreementState.inPerson.realOrderNo, orderNo, totals, paymentSummary]);

// Send reminder SMS
    const sendReminder = useCallback(async () => {
        if (!agreementState.sms.sent || agreementState.sms.reminderCount >= 2) {
            return { success: false, error: 'Cannot send reminder' };
        }

        try {
            const response = await fetch('/api/agreements/reminder', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    signingToken: agreementState.sms.signingToken,
                    customerPhone: agreementState.sms.phoneNumber,
                    customerName: customerInfo.customerName,
                    orderNo: orderNo,
                    reminderNumber: agreementState.sms.reminderCount + 1
                })
            });

            const result = await sendAgreementReminder({
                signingToken: agreementState.sms.signingToken,
                customerPhone: agreementState.sms.phoneNumber,
                customerName: customerInfo.customerName,
                orderNo: orderNo,
                reminderNumber: agreementState.sms.reminderCount + 1
              });

            if (result.success) {
                setAgreementState(prev => ({
                    ...prev,
                    sms: {
                        ...prev.sms,
                        reminderCount: prev.sms.reminderCount + 1,
                        lastReminderAt: getCurrentTimestamp()
                    }
                }));

                return { success: true, message: result.message };
            } else {
                throw new Error(result.error);
            }

        } catch (error) {
            console.error('Failed to send reminder:', error);
            return { success: false, error: error.message };
        }
    }, [agreementState.sms, customerInfo.customerName, orderNo]);

    // Retry SMS sending
    const retrySMS = useCallback(() => {
        if (agreementState.retryCount >= maxRetries) {
            return { success: false, error: 'Maximum retries exceeded' };
        }
        return sendSMSAgreement();
    }, [agreementState.retryCount, sendSMSAgreement]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // Initialize agreement requirement check
    useEffect(() => {
        checkAgreementRequirement();
    }, [checkAgreementRequirement]);

    // 🔧 NEW: Save agreement state to localStorage as backup when signed
    useEffect(() => {
        if (orderNo && agreementState.isSigned) {
            const agreementBackup = {
                orderNo,
                isSigned: agreementState.isSigned,
                signedAt: agreementState.signedAt,
                signingMethod: agreementState.signingMethod,
                googleDriveFile: agreementState.googleDriveFile,
                status: agreementState.status,
                timestamp: Date.now()
            };
            localStorage.setItem(`agreement_backup_${orderNo}`, JSON.stringify(agreementBackup));
            console.log('💾 Agreement state backed up to localStorage:', orderNo);
        }
    }, [orderNo, agreementState.isSigned, agreementState.signedAt, agreementState.signingMethod]);

    // 🔧 NEW: Restore agreement state from localStorage backup on initialization
    useEffect(() => {
        if (orderNo && !savedAgreementState?.isSigned) {
            const backupKey = `agreement_backup_${orderNo}`;
            const savedBackup = localStorage.getItem(backupKey);

            if (savedBackup) {
                try {
                    const parsed = JSON.parse(savedBackup);
                    const ageInHours = (Date.now() - parsed.timestamp) / (1000 * 60 * 60);

                    // Only restore if backup is less than 48 hours old
                    if (ageInHours < 48) {
                        console.log('🔄 Restoring agreement state from localStorage backup:', orderNo);
                        setAgreementState(prev => ({
                            ...prev,
                            isSigned: parsed.isSigned,
                            signedAt: parsed.signedAt,
                            signingMethod: parsed.signingMethod,
                            googleDriveFile: parsed.googleDriveFile,
                            status: parsed.status
                        }));
                    } else {
                        // Cleanup old backup
                        localStorage.removeItem(backupKey);
                        console.log('🧹 Removed expired agreement backup:', orderNo);
                    }
                } catch (error) {
                    console.error('Error restoring agreement backup:', error);
                    localStorage.removeItem(backupKey);
                }
            }
        }
    }, [orderNo, savedAgreementState?.isSigned]);

    return {
        // State
        agreementState,
        
        // Actions
        sendSMSAgreement,
        sendReminder,
        retrySMS,
        openInPersonSigning,
        closeInPersonSigning,
        handleInPersonSignature,
        
        // 🔧 ONLY include functions that exist in this hook
        isGeneratingOrderNo,
        ensureRealOrderNumber,
        
        // Computed values
        isRequired: agreementState.isRequired,
        isSigned: agreementState.isSigned,
        canProceed: !agreementState.isRequired || agreementState.isSigned,
        
        // Status helpers
        isWaitingForSMS: agreementState.status === 'sms_sent',
        isSMSSigned: agreementState.status === 'sms_signed',
        isInPersonSigned: agreementState.status === 'in_person_signed',
        hasExpired: agreementState.status === 'expired',
        hasFailed: agreementState.status === 'failed',
        needsFallback: ['failed', 'expired'].includes(agreementState.status) || 
                      agreementState.inPerson.fallbackReason !== null
        
                      
    };
};

export default useAgreementManager;